import {
  accounts,
  depot,
  goals,
  household as householdApi,
  reports,
  sparziele,
  type Account,
  type DepotAccount,
  type GoalNode,
  type Household,
  type Reconciliation,
  type SparzielRow,
} from "../api/client";
import { useSWR } from "../lib/swr";

/**
 * Widget data hooks on top of the persistent stale-while-revalidate cache
 * (lib/swr.ts).
 *
 * Widgets fetch their own data, so removing a widget means its API calls
 * simply never happen — but two widgets needing the same resource (e.g.
 * Kontostände + Gesamtvermögen both need accounts) share one request
 * (in-flight dedupe per key). Unlike the old per-mount cache, values now
 * survive navigation AND app restarts: the dashboard paints instantly with
 * the last known data and revalidates in the background. Pull-to-refresh
 * revalidates in place via refreshAll() — no full page reload anymore.
 */

/** Kept as a passthrough so the Dashboard tree doesn't need to change —
 *  the cache is global now, a provider scope is no longer needed. */
export function DashboardDataProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/** Cache-first widget data. Returns the last known value immediately (null
 *  only before the first ever successful fetch); errors keep stale data —
 *  the Übersicht must never white-screen because one widget's endpoint
 *  failed. */
export function useWidgetData<T>(key: string, fetcher: () => Promise<T>): T | null {
  const { data } = useSWR<T>(`w:${key}`, fetcher);
  return data ?? null;
}

// ----- Shared resources (used by more than one widget) -----

export interface AccountWithRec {
  acc: Account;
  rec: Reconciliation;
}

export function useRecs(): AccountWithRec[] | null {
  return useWidgetData("recs", async () => {
    const a = await accounts.list();
    const r = await Promise.all(a.map((x) => reports.reconciliation(x.id)));
    return a.map((acc, i) => ({ acc, rec: r[i] }));
  });
}

export function useGoalTree(): GoalNode[] | null {
  return useWidgetData("goals", () => goals.tree());
}

export function useHousehold(): Household | null {
  return useWidgetData("household", () => householdApi.get());
}

export function useSparziele(): SparzielRow[] | null {
  return useWidgetData("sparziele", () => sparziele.list());
}

export function useDepots(): DepotAccount[] | null {
  return useWidgetData("depots", () => depot.listAccounts());
}
